import React, { useState } from "react";
import firebase from "firebase/compat/app";
import { Controller, useForm } from "react-hook-form";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  TextField,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

const PasswordSettings = ({ user, visible, close, setSubmitted }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });
  const [loading, setLoading] = useState(false);
  const [firebaseError, setFirebaseError] = useState(null);

  const newPassword = watch("newPassword");

  const onSubmit = async (data) => {
    setLoading(true);
    setFirebaseError(null);

    try {
      const credential = firebase.auth.EmailAuthProvider.credential(
        user.email,
        data.currentPassword
      );
      await user.reauthenticateWithCredential(credential);
      await user.updatePassword(data.newPassword);

      reset();
      setSubmitted("Your password has been updated.");
      close();
    } catch (error) {
      switch (error.code) {
        case "auth/wrong-password":
        case "auth/invalid-credential":
          setFirebaseError("Current password is incorrect.");
          break;
        case "auth/weak-password":
          setFirebaseError("New password must be at least 6 characters.");
          break;
        case "auth/requires-recent-login":
          setFirebaseError(
            "For security reasons, please sign out and sign in again before making this change."
          );
          break;
        default:
          setFirebaseError(`An error occurred: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setFirebaseError(null);
    close();
  };

  return (
    <Dialog open={visible} onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Enter your current password and choose a new one.
          </DialogContentText>

          <Controller
            name="currentPassword"
            control={control}
            rules={{ required: "Current password is required." }}
            render={({ field }) => (
              <TextField
                label="Current Password"
                type="password"
                InputLabelProps={{ shrink: true }}
                variant="standard"
                fullWidth
                error={!!errors?.currentPassword}
                helperText={errors?.currentPassword?.message}
                sx={{ mb: 2 }}
                {...field}
              />
            )}
          />

          <Controller
            name="newPassword"
            control={control}
            rules={{
              required: "New password is required.",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters.",
              },
            }}
            render={({ field }) => (
              <TextField
                label="New Password"
                type="password"
                InputLabelProps={{ shrink: true }}
                variant="standard"
                fullWidth
                error={!!errors?.newPassword}
                helperText={errors?.newPassword?.message}
                sx={{ mb: 2 }}
                {...field}
              />
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: "Please confirm your new password.",
              validate: (value) =>
                value === newPassword || "Passwords do not match.",
            }}
            render={({ field }) => (
              <TextField
                label="Confirm New Password"
                type="password"
                InputLabelProps={{ shrink: true }}
                variant="standard"
                fullWidth
                error={!!errors?.confirmPassword}
                helperText={errors?.confirmPassword?.message}
                sx={{ mb: 2 }}
                {...field}
              />
            )}
          />

          {firebaseError && (
            <FormHelperText error sx={{ mt: 1 }}>
              {firebaseError}
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <LoadingButton
            loading={loading}
            type="submit"
            disabled={loading}
            autoFocus
            variant="contained"
          >
            Update Password
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PasswordSettings;
